import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { 
  CheckCircle2, 
  Circle, 
  ArrowRight, 
  Building2, 
  Landmark, 
  Wallet, 
  Server, 
  Users,
  Sparkles,
  AlertCircle,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { api } from '../../lib/api';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';

interface SetupStep {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  url: string;
}

interface SetupStatus {
  hasEntity: boolean;
  hasBankConnection: boolean;
  hasBolsaoAccount: boolean;
  hasProviderInstance: boolean;
  hasCorrentista: boolean;
  isComplete: boolean;
  isFullyComplete: boolean;
  completionPercentage: number;
  nextStep: string;
  nextStepUrl: string;
  steps: SetupStep[];
}

const stepIcons: Record<string, React.ElementType> = {
  'entity': Building2,
  'bank-connection': Landmark,
  'bolsao-account': Wallet,
  'provider-instance': Server,
  'correntista': Users,
};

export function SetupWizard() {
  const navigate = useNavigate();
  
  const { data: setupStatus, isLoading, error } = useQuery<SetupStatus>({
    queryKey: ['setup-status'],
    queryFn: () => api.get<SetupStatus>('/admin/setup-status').then((r: { data: SetupStatus }) => r.data),
  });

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-3 text-red-400">
          <AlertCircle className="w-5 h-5" />
          <span>Erro ao carregar status do setup</span>
        </div>
      </Card>
    );
  }

  if (!setupStatus) return null;

  // If fully complete, show success message
  if (setupStatus.isFullyComplete) {
    return (
      <Card className="p-6 border-green-500/30 bg-green-500/5">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-green-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-green-400">Setup Completo!</h3>
            <p className="text-surface-400 text-sm">
              Seu tenant está totalmente configurado e pronto para operar.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-surface-100">Configuração Inicial</h3>
          <p className="text-surface-400 text-sm">
            Complete os passos abaixo para começar a operar
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-2xl font-bold text-primary-400">
            {setupStatus.completionPercentage}%
          </div>
          <div className="text-surface-500 text-sm">completo</div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="h-2 bg-surface-800 rounded-full mb-6 overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${setupStatus.completionPercentage}%` }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="h-full bg-gradient-to-r from-primary-500 to-accent-500 rounded-full"
        />
      </div>

      {/* Steps */}
      <div className="space-y-3">
        {setupStatus.steps.map((step, index) => {
          const Icon = stepIcons[step.id] || Circle;
          const isNext = step.id === setupStatus.nextStep;
          
          return (
            <motion.div
              key={step.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className={cn(
                'flex items-center gap-4 p-4 rounded-lg transition-all cursor-pointer',
                step.completed 
                  ? 'bg-green-500/10 border border-green-500/30' 
                  : isNext
                  ? 'bg-primary-500/10 border border-primary-500/30 hover:bg-primary-500/20'
                  : 'bg-surface-800/50 border border-surface-700 hover:bg-surface-800'
              )}
              onClick={() => !step.completed && navigate(step.url)}
            >
              <div className={cn(
                'w-10 h-10 rounded-full flex items-center justify-center',
                step.completed 
                  ? 'bg-green-500/20' 
                  : isNext
                  ? 'bg-primary-500/20'
                  : 'bg-surface-700'
              )}>
                {step.completed ? (
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                ) : (
                  <Icon className={cn(
                    'w-5 h-5',
                    isNext ? 'text-primary-400' : 'text-surface-400'
                  )} />
                )}
              </div>
              
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className={cn(
                    'font-medium',
                    step.completed 
                      ? 'text-green-400' 
                      : isNext
                      ? 'text-primary-400'
                      : 'text-surface-300'
                  )}>
                    {step.title}
                  </span>
                  {isNext && (
                    <span className="text-xs bg-primary-500/20 text-primary-400 px-2 py-0.5 rounded-full">
                      Próximo
                    </span>
                  )}
                </div>
                <p className="text-sm text-surface-500">{step.description}</p>
              </div>

              {!step.completed && (
                <ArrowRight className={cn(
                  'w-5 h-5',
                  isNext ? 'text-primary-400' : 'text-surface-600'
                )} />
              )}
            </motion.div>
          );
        })}
      </div>

      {/* CTA Button */}
      {!setupStatus.isComplete && (
        <div className="mt-6 pt-6 border-t border-surface-800">
          <Button
            onClick={() => navigate(setupStatus.nextStepUrl)}
            className="w-full"
          >
            <span>Continuar Configuração</span>
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      )}
    </Card>
  );
}

// Compact version for sidebar - inline with tenant info
export function SetupProgress() {
  const navigate = useNavigate();
  
  const { data: setupStatus, isLoading } = useQuery<SetupStatus>({
    queryKey: ['setup-status'],
    queryFn: () => api.get<SetupStatus>('/admin/setup-status').then((r: { data: SetupStatus }) => r.data),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  if (isLoading || !setupStatus || setupStatus.isFullyComplete) {
    return null;
  }

  return (
    <button
      onClick={() => navigate('/admin/setup')}
      className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-primary-500/20 border border-primary-500/30 hover:bg-primary-500/30 transition-colors flex-shrink-0"
      title={`Setup ${setupStatus.completionPercentage}% completo - Clique para continuar`}
    >
      <div className="w-12 h-1.5 bg-surface-800 rounded-full overflow-hidden">
        <div 
          className="h-full bg-primary-500 rounded-full transition-all"
          style={{ width: `${setupStatus.completionPercentage}%` }}
        />
      </div>
      <span className="text-xs font-medium text-primary-400">{setupStatus.completionPercentage}%</span>
    </button>
  );
}
